import React, { useEffect } from "react";
import { Body1, styled } from "@iventis/styles";
import { CONTACT_URL, SUPPORT_EMAIL } from "@iventis/utilities";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations";
import { createLog } from "@iventis/observability-and-monitoring";
import { StyledHeader1, StyledErrorImage, StyledButton, StyledLink, ButtonContainer, ErrorContainer } from "./error-boundary-styles";

/**
 * Error boundary fallback component for WebGL-related errors.
 * Always has reload action.
 */
export const ErrorBoundaryFallbackWebGL: React.FC = () => {
    const translate = useIventisTranslate();

    useEffect(() => {
        createLog("User encountered webgl error modal");
    }, []);

    return (
        <WebGLErrorOverlay role="alertdialog">
            <WebGLErrorContainer>
                <StyledErrorImage />
                <StyledHeader1>{translate(Content.errors.error_boundaries.webgl_error_title)}</StyledHeader1>
                <Body1>
                    {translate(Content.errors.error_boundaries.webgl_error_message)}
                    <br />
                    {translate(Content.errors.error_boundaries.webgl_support_message)} <StyledLink href="https://get.webgl.org/">https://get.webgl.org/</StyledLink>.
                    <br /> <br />
                    {translate(Content.errors.error_boundaries.webgl_error_contact)} <StyledLink href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</StyledLink>.
                </Body1>
                <ButtonContainer>
                    <StyledButton href={CONTACT_URL} variant="outlined">
                        {translate(Content.errors.error_boundaries.contact)}
                    </StyledButton>
                    <StyledButton onClick={() => window.location.reload()}>{translate(Content.errors.error_boundaries.reload)}</StyledButton>
                </ButtonContainer>
            </WebGLErrorContainer>
        </WebGLErrorOverlay>
    );
};

// WebGL specific error overlay with centered content
const WebGLErrorOverlay = styled.div`
    background-color: ${({ theme }) => theme.shades.two};
    width: 100%;
    height: 100%;
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const WebGLErrorContainer = styled(ErrorContainer)`
    margin: 40px auto;
    background-color: white;
    border-radius: 4px;
`;
